/**
 * userProfileMapper — maps raw API response fields to the app's Profile shape.
 *
 * This layer decouples the UI from the exact backend field names.
 */
import type { Profile } from '../types/profile';
import type { UserProfileResponse } from '../types/userProfile';
import type { UserProfileUpdateRequest } from '../types/userProfile';
import { resolveProfilePictureUrl } from './imageUrl';
import { COUNTRIES } from '../constants';
import {
  PRIVACY_ID_TO_FIELD_VISIBILITY,
  PRIVACY_ID_TO_PROFILE_VISIBILITY,
  PRIVACY_LABEL_TO_ID,
} from '../constants/privacy';
import {
  toWeightKg,
  toWeightScaleUnitId,
  normalizeWeightUnit,
  convertWeight,
} from './weightUtils';
import {
  cmToFeetInches,
  feetInchesToCm,
  toHeightScaleUnitId,
  normalizeHeightUnit,
} from './heightUtils';
import { toDateOnlyString } from './dateHelpers';

// ─── Lookup maps ──────────────────────────────────────────────────────────────

/**
 * Maps a gender label (as stored in Profile.gender) to the integer ID
 * expected by PUT /api/v1/UserProfile/me.
 * Confirm values with backend: Male=1, Female=2, Other=3.
 */
export const GENDER_ID_MAP: Record<string, number> = {
  male: 1,
  female: 2,
  other: 3,
};

/**
 * Maps a privacy label (as used in the UI) to the integer the API expects.
 * Covers all three levels — a "Buddies only" selection used to have no entry
 * here and was silently dropped from the update payload.
 */
export const PRIVACY_ACCESS_MAP: Record<string, number> = PRIVACY_LABEL_TO_ID;

/** Field-level privacy IDs → their display labels (1 Public, 2 Buddies only, 3 Only me). */
export const PRIVACY_ID_TO_LABEL: Record<number, string> = PRIVACY_ID_TO_FIELD_VISIBILITY;

/** @deprecated Prefer `toWeightKg` from weightUtils. */
export const lbsToKg = (lbs: number): number => toWeightKg(lbs, 'lbs');

/** @deprecated Prefer `toWeightKg` from weightUtils. */
export const toKg = (value: number, unit?: string): number =>
  toWeightKg(value, unit);

// ─── API → UI mapper ─────────────────────────────────────────────────────────

/**
 * Map a raw `UserProfileResponse` from the backend to the app's `Profile` interface.
 * Weight/height values are already in the user's selected unit — do not convert.
 */
export const mapApiProfileToProfile = (raw: UserProfileResponse): Profile => {
  const weightUnit = normalizeWeightUnit(
    raw.scaleUnitWeightName ?? (raw.weightScaleUnit === 2 ? 'lbs' : 'kg'),
  );
  const weightRaw = raw.weightKg ?? raw.weight ?? raw.currentWeight;
  const currentWeight =
    weightRaw != null && Number.isFinite(Number(weightRaw))
      ? Number(weightRaw)
      : undefined;

  const heightUnit = normalizeHeightUnit(
    raw.scaleUnitHeightName ?? (raw.heightScaleUnit === 4 ? 'ft' : 'm'),
  );

  const rawHeight =
    raw.height != null && Number.isFinite(Number(raw.height))
      ? Number(raw.height)
      : raw.heightCm != null && Number.isFinite(Number(raw.heightCm))
        ? Number(raw.heightCm)
        : undefined;

  let heightCm: number | undefined;
  let heightFeet = 0;
  let heightInches = 0;

  if (rawHeight != null) {
    if (heightUnit === 'ft' && rawHeight < 10) {
      const feet = Math.floor(rawHeight);
      const inches = Math.round((rawHeight - feet) * 10);
      heightFeet = feet;
      heightInches = inches;
      heightCm = feetInchesToCm(feet, inches);
    } else if (heightUnit === 'm' && rawHeight < 3) {
      heightCm = Number((rawHeight * 100).toFixed(2));
      const parts = cmToFeetInches(heightCm);
      heightFeet = parts.feet;
      heightInches = parts.inches;
    } else {
      heightCm = rawHeight;
      const parts = cmToFeetInches(heightCm);
      heightFeet = parts.feet;
      heightInches = parts.inches;
    }
  }

  const startWeight =
    raw.startWeight != null && Number.isFinite(Number(raw.startWeight))
      ? Number(raw.startWeight)
      : undefined;
  const goalWeight =
    raw.goalWeight != null && Number.isFinite(Number(raw.goalWeight))
      ? Number(raw.goalWeight)
      : undefined;

  const cleanPlaceholder = (val: unknown): string => {
    if (typeof val !== 'string') return '';
    const trimmed = val.trim();
    return trimmed.toLowerCase() === 'string' ? '' : trimmed;
  };


  const cleanFirstName = cleanPlaceholder(raw.firstName);
  const cleanLastName = cleanPlaceholder(raw.lastName);
  const cleanUserName = cleanPlaceholder(raw.userName);

  return {
    // ── Identity ─────────────────────────────────────────────────────────────
    id: String(raw.userId ?? ''),
    userId: raw.userId,
    userGuid: raw.userGuid,
    // Combine firstName and lastName, fallback to userName
    name: [cleanFirstName, cleanLastName].filter(Boolean).join(' ') || cleanUserName || '',
    firstName: cleanFirstName || undefined,
    lastName: cleanLastName || undefined,
    email: raw.email,
    bio: cleanPlaceholder(raw.bio),

    // ── Demographics ─────────────────────────────────────────────────────────
    gender: raw.gender ?? '',
    country: (function () {
      const rawC = raw.country ?? '';
      const parsedId = Number(rawC);
      if (!isNaN(parsedId) && rawC !== '') {
        const found = COUNTRIES.find(c => c.id === parsedId);
        if (found) return found.label;
      }
      return rawC;
    })(),
    dateOfBirth: raw.dateOfBirth
      ? raw.dateOfBirth.split('T')[0] // normalize to YYYY-MM-DD for display
      : undefined,

    // ── Avatar ────────────────────────────────────────────────────────────────
    avatar: (() => {
      const avatarUrl = resolveProfilePictureUrl(raw.profilePicture);
      return avatarUrl ? { uri: avatarUrl } : undefined;
    })(),

    // ── Measurements (UI values in the user's preferred units) ────────────────
    currentWeight,
    startWeight,
    startWeightUnit: weightUnit,
    goalWeight,
    goalWeightUnit: weightUnit,
    heightCm,
    heightFeet,
    heightInches,
    heightUnit,
    unit: weightUnit,
    age: raw.age,
    aboutMe: cleanPlaceholder(raw.aboutMe),

    // ── Social Counts ─────────────────────────────────────────────────────────
    postCount: raw.numberOfPosts ?? raw.postCount,
    followingCount: raw.followingCount ?? raw.following,
    followersCount: raw.followersCount ?? raw.followers,
    buddiesCount: raw.buddiesCount ?? raw.buddies,

    // ── Points ────────────────────────────────────────────────────────────────
    totalPoints: raw.totalPoints,
    weeklyPoints: raw.weeklyPoints,

    // ── Privacy ───────────────────────────────────────────────────────────────
    countryPrivacy: raw.countryPrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.countryPrivacy] : undefined,
    weightPrivacy: raw.weightPrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.weightPrivacy] : undefined,
    dateOfBirthPrivacy: raw.dateOfBirthPrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.dateOfBirthPrivacy] : undefined,
    profileViewPrivacy: raw.profileViewPrivacy !== undefined ? PRIVACY_ID_TO_PROFILE_VISIBILITY[raw.profileViewPrivacy] : undefined,
    heightPrivacy: raw.heightPrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.heightPrivacy] : undefined,
    bioPrivacy: raw.bioPrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.bioPrivacy] : undefined,
    aboutMePrivacy: raw.aboutMePrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.aboutMePrivacy] : undefined,
    genderPrivacy: raw.genderPrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.genderPrivacy] : undefined,
    profilePicturePrivacy: raw.profilePicturePrivacy !== undefined ? PRIVACY_ID_TO_LABEL[raw.profilePicturePrivacy] : undefined,
    isBuddy:
      raw.isBuddy === true ||
      (raw.isBuddy as unknown) === 1 ||
      String(raw.isBuddy).toLowerCase() === 'true' ||
      raw.isAccepted === true ||
      (raw.isAccepted as unknown) === 1 ||
      (typeof raw.status === 'string' && raw.status.trim().toLowerCase() === 'accepted') ||
      Number(raw.status) === 3,
    invited: raw.invited,
    isRequested: raw.isRequested,
    isAccepted:
      raw.isAccepted === true ||
      (raw.isAccepted as unknown) === 1 ||
      raw.isBuddy === true ||
      (raw.isBuddy as unknown) === 1 ||
      (typeof raw.status === 'string' && raw.status.trim().toLowerCase() === 'accepted') ||
      Number(raw.status) === 3,
    isFollowing:
      raw.isFollowing === true ||
      (raw.isFollowing as unknown) === 1 ||
      String(raw.isFollowing).toLowerCase() === 'true',
    isFollower:
      raw.isFollower === true ||
      (raw.isFollower as unknown) === 1 ||
      (raw as { isFollowedBy?: boolean | number }).isFollowedBy === true ||
      (raw as { isFollowedBy?: boolean | number }).isFollowedBy === 1,
    userName: raw.userName,
    status: raw.status ?? null,

    // ── AI Buddy ──────────────────────────────────────────────────────────────
    followedAiAgent: raw.followedAiAgent
      ? {
          agentId: raw.followedAiAgent.agentId ?? null,
          agentGuid:
            raw.followedAiAgent.agentGuid ||
            (raw.followedAiAgent as any).profileUserGuid ||
            (raw.followedAiAgent as any).userGuid ||
            null,
          userName: raw.followedAiAgent.userName ?? null,
          profilePicture: raw.followedAiAgent.profilePicture ?? null,
          conversationId: raw.followedAiAgent.conversationId ?? null,
        }
      : null,
  };
};

// ─── UI → API mappers ─────────────────────────────────────────────────────────

/**
 * Maps a partial `Profile` object to the request body expected by
 * `PUT /api/v1/UserProfile/me` or `UpsertProfilePrivacySettings`.
 *
 * Missing / undefined fields in the partial are omitted from the output.
 */
export const mapProfileToUpdateRequest = (
  profile: Partial<Profile>,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  if (profile.name !== undefined) {
    const parts = profile.name.trim().split(' ');
    payload.firstName = parts[0];
    if (parts.length > 1) {
      payload.lastName = parts.slice(1).join(' ');
    }
  }
  if (profile.bio !== undefined) payload.bio = profile.bio;
  if (profile.dateOfBirth !== undefined) payload.dateOfBirth = profile.dateOfBirth;

  // Gender: map string label → integer ID
  if (profile.gender !== undefined) {
    const genderId = GENDER_ID_MAP[profile.gender.toLowerCase()];
    if (genderId !== undefined) {
      payload.genderId = genderId;
    }
  }

  // Country: profile.country is a string label or code; numeric ID is stored.
  // `mapApiProfileToProfile` hands back the label, so it is resolved here —
  // otherwise a country picked from the list would be dropped from the payload.
  if (profile.country !== undefined) {
    const raw = String(profile.country).trim();
    const parsed = Number(raw);
    if (raw !== '' && !isNaN(parsed)) {
      payload.countryId = parsed;
    } else if (raw !== '') {
      const match = COUNTRIES.find(
        item => item.label.toLowerCase() === raw.toLowerCase() || item.value === raw.toUpperCase(),
      );
      if (match) payload.countryId = match.id;
    }
  }

  const privacyFields = [
    'profileViewPrivacy', 'countryPrivacy', 'weightPrivacy', 'heightPrivacy',
    'dateOfBirthPrivacy', 'genderPrivacy', 'bioPrivacy', 'aboutMePrivacy', 'profilePicturePrivacy'
  ] as const;

  privacyFields.forEach(field => {
    if (profile[field] !== undefined) {
      const accessId = PRIVACY_ACCESS_MAP[String(profile[field]).toLowerCase()];
      if (accessId !== undefined) {
        payload[field] = accessId;
      }
    }
  });

  // Height: always convert and send in centimeters (backend expects Cm for heightCm).
  if (
    profile.heightCm !== undefined ||
    profile.heightFeet !== undefined ||
    profile.heightInches !== undefined ||
    profile.heightUnit !== undefined
  ) {
    const heightUnit = normalizeHeightUnit(profile.heightUnit);
    let cm = 0;
    if (heightUnit === 'ft') {
      const feet =
        profile.heightFeet ??
        (profile.heightCm ? cmToFeetInches(profile.heightCm).feet : 0);
      const inches =
        profile.heightInches ??
        (profile.heightCm ? cmToFeetInches(profile.heightCm).inches : 0);
      cm = feetInchesToCm(feet, inches);
    } else {
      const rawVal = profile.heightCm != null ? Number(profile.heightCm) : 0;
      cm = rawVal < 10 ? rawVal * 100 : rawVal;
    }
    payload.heightCm = Number(cm.toFixed(2));
    payload.heightScaleUnit = toHeightScaleUnitId(heightUnit);
  }

  // Weight: normalize StartWeight and GoalWeight into the common WeightScaleUnit of CurrentWeight.
  const targetWeightUnitId = toWeightScaleUnitId(profile.unit);

  if (profile.currentWeight !== undefined) {
    payload.weightKg = Number(Number(profile.currentWeight).toFixed(2));
    payload.weightScaleUnit = targetWeightUnitId;
  }

  if (profile.startWeight !== undefined || (profile as any).startWeightKg !== undefined) {
    const rawStart = profile.startWeight ?? (profile as any).startWeightKg;
    const startUnitId = profile.startWeightUnit
      ? toWeightScaleUnitId(profile.startWeightUnit)
      : targetWeightUnitId;
    const convertedStartWeight = convertWeight(
      Number(rawStart),
      startUnitId,
      targetWeightUnitId,
    );
    const startVal = Number(Number(convertedStartWeight).toFixed(2));
    payload.startWeight = startVal;
    payload.startWeightKg = startVal;
  }

  if (profile.goalWeight !== undefined || (profile as any).goalWeightKg !== undefined) {
    const rawGoal = profile.goalWeight ?? (profile as any).goalWeightKg;
    const goalUnitId = profile.goalWeightUnit
      ? toWeightScaleUnitId(profile.goalWeightUnit)
      : targetWeightUnitId;
    const convertedGoalWeight = convertWeight(
      Number(rawGoal),
      goalUnitId,
      targetWeightUnitId,
    );
    const goalVal = Number(Number(convertedGoalWeight).toFixed(2));
    payload.goalWeight = goalVal;
    payload.goalWeightKg = goalVal;
  }

  if (profile.avatar && typeof profile.avatar === 'object' && 'uri' in profile.avatar) {
    payload.profilePicture = (profile.avatar as { uri: string }).uri;
  }
  // Browsers upload the real File/Blob. It does not need a transient blob URL
  // in profile state, and keeping the two fields independent avoids that leak.
  if (profile.avatarBlob) payload.profilePictureBlob = profile.avatarBlob;

  return payload;
};

/**
 * Build a minimal API request payload for a **single field** update.
 *
 * This is the primary helper used by sub-screens so they can send only the
 * changed field to `PUT /api/v1/UserProfile/me`, without needing to construct
 * a full Profile object.
 *
 * @example
 *   // Gender update
 *   buildUpdatePayload('gender', 'Male')  →  { gender: 1 }
 *
 *   // Weight update — sends selected value + unit (backend converts)
 *   buildUpdatePayload('currentWeight', 150, 'lbs')  →  { weightKg: 150, weightScaleUnit: 2 }
 *
 *   // Country update (expects numeric ID string or number)
 *   buildUpdatePayload('country', 5172)  →  { country: 5172 }
 *
 *   // Date of birth
 *   buildUpdatePayload('dateOfBirth', '1995-05-14')  →  { dateOfBirth: '1995-05-14' }
 *
 *   // Privacy
 *   buildUpdatePayload('privacyAccess', 'Public')  →  { privacyAccess: 1 }
 *
 *   // Profile picture
 *   buildUpdatePayload('profilePicture', 'file:///...')  →  { profilePicture: 'file:///...' }
 */
export const buildUpdatePayload = (
  field: string,
  value: string | number,
  unit?: string,
): Partial<UserProfileUpdateRequest> => {
  switch (field) {
    case 'gender': {
      const label = String(value).toLowerCase();
      const id = GENDER_ID_MAP[label];
      return id !== undefined ? { genderId: id } : {};
    }

    case 'country': {
      const id = Number(value);
      return !isNaN(id) ? { countryId: id } : {};
    }

    case 'currentWeight': {
      return {
        weightKg: Number(Number(value).toFixed(2)),
        weightScaleUnit: toWeightScaleUnitId(unit),
      };
    }

    case 'dateOfBirth': {
      // Normalize to YYYY-MM-DD using local calendar parts (never toISOString).
      const raw = String(value);
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return { dateOfBirth: raw };
      }
      const dateOnly = toDateOnlyString(raw);
      return { dateOfBirth: dateOnly || raw };
    }

    case 'profileViewPrivacy':
    case 'countryPrivacy':
    case 'weightPrivacy':
    case 'heightPrivacy':
    case 'dateOfBirthPrivacy':
    case 'genderPrivacy':
    case 'bioPrivacy':
    case 'aboutMePrivacy':
    case 'profilePicturePrivacy': {
      const key = String(value).toLowerCase();
      const id = PRIVACY_ACCESS_MAP[key];
      return id !== undefined ? { [field]: id } : {};
    }

    case 'profilePicture': {
      return { profilePicture: String(value) };
    }

    case 'displayName': {
      const parts = String(value).trim().split(' ');
      const payload: Partial<UserProfileUpdateRequest> = { firstName: parts[0] };
      if (parts.length > 1) {
        payload.lastName = parts.slice(1).join(' ');
      }
      return payload;
    }

    case 'bio': {
      return { bio: String(value) };
    }

    case 'height':
    case 'heightCm': {
      const heightUnit = normalizeHeightUnit(unit);
      let cm = 0;
      const numVal = Number(value);
      if (heightUnit === 'ft' && numVal < 10) {
        const feet = Math.floor(numVal);
        const inches = Math.round((numVal - feet) * 10);
        cm = feetInchesToCm(feet, inches);
      } else if (heightUnit === 'm' && numVal < 3) {
        cm = numVal * 100;
      } else {
        cm = numVal;
      }
      return {
        heightCm: Number(cm.toFixed(2)),
        heightScaleUnit: toHeightScaleUnitId(heightUnit),
      };
    }

    default:
      return {};
  }
};


